import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Facebook, Instagram, Twitter, Youtube, Send, Ship } from 'lucide-react';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { toast } from '../hooks/use-toast';
import { useSettings } from '../context/SettingsContext';

const Footer = () => {
  const [email, setEmail] = useState('');
  const settings = useSettings();

  const subscribe = (e) => {
    e.preventDefault();
    if (!email) return;
    toast({ title: 'Subscribed', description: 'You are on our journey list. Welcome aboard!' });
    setEmail('');
  };

  return (
    <footer className="bg-[#1e3454] text-white">
      <div className="border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-14 grid md:grid-cols-2 gap-8 items-center">
          <div>
            <h3 className="font-serif text-2xl md:text-3xl leading-tight">Set sail with exclusive Nile deals</h3>
            <p className="text-white/70 mt-2">Join 15,000+ travelers receiving early-access itineraries and seasonal savings.</p>
          </div>
          <form onSubmit={subscribe} className="flex gap-2">
            <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Your email address" className="bg-white/10 border-white/20 text-white placeholder:text-white/50 h-12" />
            <Button type="submit" className="bg-[#a6b2b5] hover:bg-white hover:text-[#304b71] text-[#304b71] h-12 px-6">
              Subscribe <Send size={14} className="ml-1" />
            </Button>
          </form>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-16 grid md:grid-cols-2 lg:grid-cols-4 gap-10">
        <div>
          <div className="flex items-center gap-2 mb-5">
            <div className="w-11 h-11 rounded-full bg-white text-[#304b71] flex items-center justify-center">
              <Ship size={22} />
            </div>
            <div>
              <div className="font-serif text-lg">{settings.brand}</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#a6b2b5]">Egypt · Since 1998</div>
            </div>
          </div>
          <p className="text-white/70 text-sm leading-relaxed">{settings.tagline}. Curated Nile itineraries, expert Egyptologists and unbeatable prices — straight from the source.</p>
          <div className="flex gap-2 mt-6">
            {[[Facebook, settings.social_facebook], [Instagram, settings.social_instagram], [Twitter, settings.social_twitter], [Youtube, settings.social_youtube]].map(([I, url], i) => (
              <a key={i} href={url || '#'} className="w-9 h-9 rounded-full border border-white/20 flex items-center justify-center hover:bg-[#a6b2b5] hover:text-[#304b71] hover:border-transparent transition-colors">
                <I size={15} />
              </a>
            ))}
          </div>
        </div>

        <div>
          <h4 className="font-serif text-lg mb-5">Cruise Categories</h4>
          <ul className="space-y-3 text-white/70 text-sm">
            {['Luxury Nile', 'Deluxe Nile River', 'Standard Nile River', 'Dahabiya Nile'].map((c) => (
              <li key={c}><Link to={`/cruises?category=${encodeURIComponent(c)}`} className="hover:text-[#a6b2b5] transition-colors">{c} Cruises</Link></li>
            ))}
            <li><Link to="/cruises" className="hover:text-[#a6b2b5] transition-colors">All Nile Cruises</Link></li>
          </ul>
        </div>

        <div>
          <h4 className="font-serif text-lg mb-5">Egypt Tours</h4>
          <ul className="space-y-3 text-white/70 text-sm">
            <li><Link to="/packages" className="hover:text-[#a6b2b5] transition-colors">All Tour Packages</Link></li>
            <li><Link to="/tours" className="hover:text-[#a6b2b5] transition-colors">Egypt Tour Categories</Link></li>
            <li><Link to="/blog" className="hover:text-[#a6b2b5] transition-colors">Travel Guide</Link></li>
            <li><Link to="/about" className="hover:text-[#a6b2b5] transition-colors">About Us</Link></li>
            <li><Link to="/request-quote" className="hover:text-[#a6b2b5] transition-colors">Request a Quote</Link></li>
          </ul>
        </div>

        <div>
          <h4 className="font-serif text-lg mb-5">Get In Touch</h4>
          <ul className="space-y-4 text-white/75 text-sm">
            <li className="flex gap-3"><MapPin size={18} className="text-[#a6b2b5] flex-shrink-0 mt-0.5" /><span>{settings.address}</span></li>
            <li className="flex gap-3 items-center"><Phone size={18} className="text-[#a6b2b5]" /><a href={`tel:${settings.phone_raw}`} className="hover:text-[#a6b2b5]">{settings.phone}</a></li>
            <li className="flex gap-3 items-center"><Mail size={18} className="text-[#a6b2b5]" /><a href={`mailto:${settings.email}`} className="hover:text-[#a6b2b5]">{settings.email}</a></li>
          </ul>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-6 flex flex-col md:flex-row justify-between items-center gap-3 text-xs text-white/60">
          <div>© {new Date().getFullYear()} {settings.brand}. All rights reserved.</div>
          <div className="flex gap-6">
            <a href="#" className="hover:text-white">Privacy Policy</a>
            <a href="#" className="hover:text-white">Terms & Conditions</a>
            <Link to="/admin/login" className="hover:text-white opacity-60">Admin</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
